import { z } from 'zod';

export const entityIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, 'Invalid entity identifier');

export const nonEmptyTextSchema = z.string().trim().min(1);
export const utcDateTimeSchema = z.iso.datetime({ offset: true });
export const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/i, 'Expected a SHA-256 digest');

export const organizationSnapshotSchema = z
  .object({
    organizationId: entityIdSchema,
    organizationName: nonEmptyTextSchema.max(200),
    organizationType: z.enum(['tenant', 'site', 'department', 'contractor']),
  })
  .strict();

export const actorSnapshotSchema = z
  .object({
    identityId: entityIdSchema,
    displayName: nonEmptyTextSchema.max(100),
    jobTitle: z.string().trim().max(100).nullable(),
    organization: organizationSnapshotSchema,
  })
  .strict();

/**
 * Immutable attribution for a legacy inspection whose source recorded only a person's name.
 * This deliberately has no `identityId`, organization ID, authentication state, role or
 * capability. A later exact-name, manually approved claim links history for reads without
 * rewriting this snapshot; `claimStateAtImport` remains immutable provenance.
 */
export const historicalInspectionActorSnapshotSchema = z
  .object({
    actorKind: z.literal('historical-inspection-person'),
    historicalPersonId: entityIdSchema,
    displayName: nonEmptyTextSchema.max(100),
    legacyOrganizationName: nonEmptyTextSchema.max(200),
    normalizedName: nonEmptyTextSchema.max(100),
    normalizedNameVersion: z.literal('ko-exact-name-v1'),
    authenticationAllowed: z.literal(false),
    claimStateAtImport: z.literal('unclaimed'),
    claimPolicy: z.literal('manual-approval-exact-name-v1'),
  })
  .strict();

export const inspectionActorSnapshotSchema = z.union([
  actorSnapshotSchema,
  historicalInspectionActorSnapshotSchema,
]);

export const attachmentMetadataSchema = z
  .object({
    attachmentId: entityIdSchema,
    fileName: nonEmptyTextSchema.max(255),
    contentType: nonEmptyTextSchema.max(100),
    sizeBytes: z.number().int().nonnegative(),
    sha256: sha256Schema,
    capturedAt: utcDateTimeSchema.nullable(),
  })
  .strict();

export type ActorSnapshot = z.infer<typeof actorSnapshotSchema>;
export type HistoricalInspectionActorSnapshot = z.infer<
  typeof historicalInspectionActorSnapshotSchema
>;
export type InspectionActorSnapshot = z.infer<typeof inspectionActorSnapshotSchema>;
export type AttachmentMetadata = z.infer<typeof attachmentMetadataSchema>;
export type OrganizationSnapshot = z.infer<typeof organizationSnapshotSchema>;
